// Hideo - script editor's writing surface: paste, copy and cut on the page. The cleaning paths are
// src/features/editor/paste.ts (pasteCopy, pasteTidy, autoLinkIn) and html.ts (cleanHtml); this is the event side: the
// clipboard, the browser commands and the marker of what was copied from a Hideo script. Ported from the legacy
// editor/editor.js with the same steps; a pasted copy no longer duplicates comment highlights (dropDupComments).
import { esc } from '../../../utils/html'
import { useTocStore } from '../../../stores/toc'
import { refreshChips } from '../../mentions'
import { cleanHtml, fixLinks, liftLists, noZw, stripLowc, wrapLoneItems } from '../html'
import { LONE_URL, autoLinkIn, linkHref, pasteCopy, pasteSpaces, pasteTidy } from '../paste'
import { mdToHtml } from '../markdown'
import { edBody, queueScriptSave } from './core'
import { hideBlockHandle } from './blocks'

/** Ctrl+Shift+V was pressed: the next paste is plain text (typing.ts sets it for 400 ms) */
export const paste = { plain: false }

/** A Hideo copy pasted back: commented text whose comment is still highlighted in the page loses its highlight (the
 *  comment stays on the original text; before, the copy got a second highlight with the same id). After a cut the
 *  original is gone, so the pasted text keeps it. */
function dropDupComments(root: ParentNode, body: ParentNode = edBody()): void {
  root.querySelectorAll<HTMLElement>('span.cmt[data-cmt]').forEach(sp => {
    if (body.querySelector(`span.cmt[data-cmt="${CSS.escape(sp.dataset.cmt as string)}"]`)) sp.replaceWith(...sp.childNodes)
  })
}
/** Inserts pasted HTML at the cursor: trusted = written by Hideo itself (a copy from a script, links it built), else
 *  only the allowed elements are copied and tidied, and the spaces of the source's HTML layout go (plain: the
 *  clipboard's text, for the spaces at the edges); addresses become links */
export function pasteInsertHtml(html: string, trusted?: boolean, plain?: string): void {
  const tpl = document.createElement('template'); tpl.innerHTML = html
  let root: DocumentFragment = tpl.content
  if (!trusted) { root = document.createDocumentFragment(); pasteCopy(tpl.content, root); pasteTidy(root); pasteSpaces(root, plain) }
  else dropDupComments(root)
  wrapLoneItems(root)
  autoLinkIn(root)
  const d = document.createElement('div'); d.appendChild(root)
  if (!d.innerHTML.trim()) return
  document.execCommand('insertHTML', false, d.innerHTML)
  if (edBody().querySelector('blockquote > ul, blockquote > ol')) liftLists()   // a list pasted in a quote comes out of it
  fixLinks(); if (trusted) refreshChips(edBody()); queueScriptSave(); useTocStore().queue()
}
/** Plain text pasted: a lone address becomes a link (on selected text, its link), one line is text with its addresses
 *  as links, several lines are Markdown */
export function pasteText(txt: string): void {
  txt = txt.replace(/\r/g, '')
  const one = txt.trim(), s = getSelection() as Selection
  const h = LONE_URL.test(one) ? linkHref(one) : ''
  if (h) {   // a single address: on selected text it becomes its link
    if (s.rangeCount && !s.isCollapsed && s.toString().trim()) { document.execCommand('createLink', false, h); fixLinks(); queueScriptSave(); return }
    return pasteInsertHtml(`<a href="${esc(h)}">${esc(one)}</a>&nbsp;`, true)
  }
  if (!txt.includes('\n')) {
    const tmp = document.createElement('div'); tmp.textContent = txt; autoLinkIn(tmp)
    if (!tmp.querySelector('a')) { document.execCommand('insertText', false, txt); return }
    return pasteInsertHtml(tmp.innerHTML, true)
  }
  // Several lines: Markdown becomes formatting (headings, lists, bold…). "<" and "&" stay text.
  let fence = false
  const safe = txt.split('\n').map(l => { if (/^```/.test(l)) { fence = !fence; return l } return fence ? l : l.replace(/&/g, '&amp;').replace(/</g, '&lt;') }).join('\n')
  pasteInsertHtml(mdToHtml(safe))
}
function onPaste(e: ClipboardEvent): void {
  e.preventDefault(); hideBlockHandle()
  const cd = e.clipboardData as DataTransfer, html = cd.getData('text/html'), txt = cd.getData('text/plain')
  if (paste.plain) { paste.plain = false; document.execCommand('insertText', false, txt); return }   // Ctrl+Shift+V = plain text
  if (html.includes('<!--hideo-->')) return pasteInsertHtml(cleanHtml(html.replace('<!--hideo-->', '')), true)   // copied from a Hideo script
  if (html && /<[a-z]/i.test(html) && !LONE_URL.test(txt.trim())) return pasteInsertHtml(html, false, txt)
  pasteText(txt)
}
/** Copy / cut inside the editor: carries colours, callouts and mentions too (with a marker that recognises it) */
export function edCopy(e: ClipboardEvent, cut: boolean): void {
  const s = getSelection() as Selection
  if (!s.rangeCount || s.isCollapsed) return
  const r = s.getRangeAt(0), anc = r.commonAncestorContainer
  // Some items of one list: they keep their list (alone, the items lost their dots and went into quotes)
  const d = document.createElement('div'), list = /^(UL|OL)$/.test(anc.nodeName) ? anc.cloneNode(false) as HTMLElement : null
  if (list) { list.appendChild(r.cloneContents()); d.appendChild(list) } else d.appendChild(r.cloneContents())
  ;(e.clipboardData as DataTransfer).setData('text/html', '<!--hideo-->' + noZw(stripLowc(d.innerHTML)))
  ;(e.clipboardData as DataTransfer).setData('text/plain', noZw(s.toString()))
  e.preventDefault()
  if (cut) { document.execCommand('delete'); queueScriptSave() }
}
/** The page's paste, copy and cut listeners (in the legacy order) */
export function installClipboard(body: HTMLElement): void {
  body.addEventListener('paste', onPaste)
  body.addEventListener('copy', e => edCopy(e, false))
  body.addEventListener('cut', e => edCopy(e, true))
}
